import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";

/**
 * One spoken line, as the live caption overlay needs it.
 */
export interface LiveCaptionLine {
    start: number;
    end: number;
    text: string;
}

/** Seconds either side of the playhead that one request covers. */
const BEFORE_SECONDS = 30;
const AFTER_SECONDS = 300;

/**
 * Bucket the playhead so a request is made per *minute* of playback, not per
 * frame. Time updates arrive every 50 ms; without this the page would issue
 * twenty queries a second.
 */
const BUCKET_SECONDS = 60;

/**
 * Readable-line thresholds.
 *
 * Auto-generated tracks roll: when a slide advances by one word the worker keeps
 * only the delta, so a stored "line" can be `"the"`. Shown verbatim the caption
 * reads `"one is the the the the the"`. Fragments are therefore glued back
 * together up to a sentence end or a sane maximum, which fixes display for
 * transcripts already imported — no re-fetch.
 */
const MIN_WORDS = 4;
const MAX_WORDS = 14;
const ENDS_SENTENCE = /[.!?]["')\]]?$/;

interface CaptionWindowRow {
    start_seconds: number;
    end_seconds: number;
    text: string;
}

/**
 * Caption lines around the current playback position.
 *
 * The page used to show only the one line that matched the search, which stops
 * being true the moment you press play — the video keeps talking and the
 * highlighted line goes stale. This fetches the transcript for the minute being
 * played so the caption can follow along, exactly like the subtitles it replaces.
 *
 * Fetched as a window rather than a whole transcript: a long podcast is a few
 * thousand lines and the reader needs the minute around the playhead. React
 * Query keys the window by playback minute, so scrubbing backwards to a minute
 * already heard is instant and costs nothing.
 */
export function useCaptionWindow(params: {
    source: "library" | "imported";
    videoRowId: string;
    /** Current playback position in seconds. */
    at: number;
    enabled: boolean;
}) {
    const { source, videoRowId, at, enabled } = params;

    const bucket = Math.floor(Math.max(at, 0) / BUCKET_SECONDS);

    const query = useQuery({
        queryKey: ["caption-window", source, videoRowId, bucket],
        enabled: enabled && Boolean(videoRowId),
        queryFn: async (): Promise<CaptionWindowRow[]> => {
            const query = new URLSearchParams({
                source,
                videoRowId,
                at: String(bucket * BUCKET_SECONDS),
                before: String(BEFORE_SECONDS),
                after: String(AFTER_SECONDS),
            });

            return await apiGet<CaptionWindowRow[]>(`/v1/videos/captions/window?${query.toString()}`);
        },
        // A transcript does not change; re-requesting one already held is waste.
        staleTime: 10 * 60 * 1000,
        // Keep the previous window on screen while the next loads, otherwise the
        // caption blinks out every time playback crosses a minute boundary.
        placeholderData: (previous) => previous,
    });

    const lines = useMemo<LiveCaptionLine[]>(
        () =>
            (query.data ?? []).map((row) => ({
                start: Number(row.start_seconds),
                end: Number(row.end_seconds),
                text: row.text,
            })),
        [query.data],
    );

    /** Fragments glued into readable display lines, each spanning its parts. */
    const displayLines = useMemo<LiveCaptionLine[]>(() => {
        const merged: LiveCaptionLine[] = [];
        let buffer: LiveCaptionLine | null = null;

        for (const line of lines) {
            buffer = buffer
                ? { start: buffer.start, end: line.end, text: `${buffer.text} ${line.text}` }
                : { ...line };

            const words = buffer.text.trim().split(/\s+/).length;
            if (ENDS_SENTENCE.test(buffer.text.trim()) ? words >= MIN_WORDS : words >= MAX_WORDS) {
                merged.push(buffer);
                buffer = null;
            }
        }

        if (buffer) merged.push(buffer);
        return merged;
    }, [lines]);

    /**
     * The line being spoken right now, or the most recent one that has finished.
     *
     * Falling back to the last started line matters at a gap between captions:
     * without it the overlay flickers empty in every silence, which reads as a
     * broken caption rather than a pause in speech.
     *
     * The *index* comes back with the line because the caption strip renders the
     * lines either side of it as a lyrics window — the neighbours have to be
     * resolved against the same array, not guessed from timestamps.
     */
    const active = useMemo<{ line: LiveCaptionLine | null; index: number }>(() => {
        if (displayLines.length === 0) return { line: null, index: -1 };

        let index = -1;
        for (let position = 0; position < displayLines.length; position += 1) {
            if (displayLines[position].start <= at) index = position;
            else break;
        }

        if (index === -1) return { line: null, index: -1 };

        // Drop a line that finished long ago rather than leaving stale words up
        // through a long silence.
        const current = displayLines[index];
        if (at - current.end > 8) return { line: null, index: -1 };

        return { line: current, index };
    }, [displayLines, at]);

    return {
        lines: displayLines,
        activeLine: active.line,
        activeIndex: active.index,
        isLoading: query.isLoading,
    };
}
